import amplify from '@services/amplify';
import {
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  confirmSignUp as amplifyConfirmSignUp,
  fetchAuthSession as amplifyFetchAuthSession,
  resendSignUpCode,
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signInWithRedirect as amplifySignInWithRedirect,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  SignUpOutput,
} from 'aws-amplify/auth';

const COGNITO_ERROR_CODES = {
  CodeMismatchException: 'Code did not match. Please try again.',
  ExpiredCodeException: 'Code expired. Please try the forgot password process again.',
  TooManyFailedAttemptsException: 'Too many failed attempts. Try again later.',
  UserNotConfirmedException: 'This account must be confirmed first.',
  _DEFAULT: 'Sorry, something went wrong on our end. Our team is looking into it!',
};

const ERROR_CODES: Record<string, string> = {
  ...COGNITO_ERROR_CODES,
};

async function confirmSignIn({ code }: { code: string }) {
  return await amplifyConfirmSignIn({ challengeResponse: code });
}

async function confirmSignUp({ email, code }: { email: string; code: string }): Promise<boolean> {
  try {
    const result = await amplifyConfirmSignUp({ confirmationCode: code, username: email });
    if (result.nextStep.signUpStep === 'DONE' || result.nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
      return true;
    }
  } catch (err: any) {
    // Fall through and return false to signal sign in failure.
  }
  return false;
}

async function forgotPassword(email: string) {
  return amplifyResetPassword({ username: email });
}

async function forgotPasswordSubmit(email: string, code: string, newPassword: string) {
  return amplifyConfirmResetPassword({ confirmationCode: code, newPassword, username: email });
}

async function getCurrentSession() {
  return await amplifyFetchAuthSession();
}

async function resendCode(email: string): Promise<{ destination?: string; success: boolean }> {
  try {
    const result = await resendSignUpCode({ username: email });
    return { destination: result.destination, success: true };
  } catch (err) {
    return { success: false };
  }
}

async function signOut(): Promise<void> {
  try {
    await amplifySignOut();
  } catch (_) {
    // noop
  }
}

interface SignUpProps {
  attributes: {
    email: string;
    familyName: string;
    givenName: string;
    phoneNumber: string; // TODO: must be changed to support other country codes
  };
  password: string;
  username: string;
}

async function signUp(props: SignUpProps): Promise<{ success: boolean; signupResult?: SignUpOutput }> {
  try {
    const signupResult = await amplifySignUp({
      options: {
        autoSignIn: true,
        userAttributes: {
          email: props.attributes.email.trim(),
          family_name: props.attributes.familyName.trim(),
          given_name: props.attributes.givenName.trim(),
          phone_number: '+1' + props.attributes.phoneNumber.match(/\d+/g)?.join(''),
        },
      },
      password: props.password,
      username: props.attributes.email.trim(),
    });
    return {
      signupResult,
      success: true,
    };
  } catch (err: any) {
    return { success: false };
  }
}

async function signIn({ password, username }: { password: string; username: string }) {
  return amplifySignIn({ password, username });
}

async function signInWithOIDC({ provider, customState }: { provider: { custom: string }; customState?: string }) {
  return amplifySignInWithRedirect({
    customState,
    provider,
  });
}

let hasInitialized = false;
export default (() => {
  if (!hasInitialized) {
    amplify;
    hasInitialized = true;
  }

  return {
    ERROR_CODES,
    confirmSignIn,
    confirmSignUp,
    forgotPassword,
    forgotPasswordSubmit,
    getCurrentSession,
    resendCode,
    signIn,
    signInWithOIDC,
    signOut,
    signUp,
  };
})();
